import axios from 'axios';
import { beginSupportTransition, clearSupportSession, getActiveToken, isSupportTransitioning, readSupportSession, supportReturnPath } from './supportSession';

// The production build is served by the API process, so same-origin `/api`
// works without baking a deployment-specific hostname into the client bundle.
// Keep the separate localhost server as the development fallback.
const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');

// Logo/asset URLs from the server are either already-absolute (Cloudflare R2)
// or relative to the API's origin (local disk fallback, e.g. /uploads/logos/x.png).
// Blindly prefixing every URL with the API origin breaks the R2 case.
export const resolveAssetUrl = (url) => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `${API_ORIGIN}${url}`;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  if (isSupportTransitioning() && !config.supportTransitionAllowed) throw new axios.CanceledError('Account switching is in progress');
  const token = getActiveToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const session = readSupportSession();
  if (session && config.headers.Authorization === `Bearer ${session.token}`) {
    config.supportSessionSnapshot = { targetRole: session.targetRole, targetUserId: session.targetUserId, id: session.id };
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (error.config?.supportSessionSnapshot) {
        if (!isSupportTransitioning()) {
          beginSupportTransition();
          clearSupportSession();
          window.location.replace(supportReturnPath(error.config.supportSessionSnapshot));
        }
        return Promise.reject(error);
      }
      clearSupportSession();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Branding endpoints
export const branding = {
  get: () => api.get('/branding'),
  updateColors: (primaryColor, secondaryColor) => api.put('/branding/colors', { primaryColor, secondaryColor }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post('/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  useCompanyWordmark: () => api.put('/branding/logo-mode', { mode: 'wordmark' }),
  deleteLogo: () => api.delete('/branding/logo'),
  updateProfile: (data) => api.put('/branding/profile', data),
  completeOnboarding: () => api.post('/branding/complete'),
};

// Flyers endpoints
export const flyers = {
  getAll: (params, config = {}) => api.get('/flyers', { ...config, params }),
  getCategories: () => api.get('/flyers/categories'),
  getSizes: () => api.get('/flyers/sizes'),
  getOne: (id) => api.get(`/flyers/${id}`),
  preview: (id, size, page = 1) => api.get(`/flyers/${id}/preview`, {
    params: { size, page },
    responseType: 'blob'
  }),
  download: (id, sizeSlug, purpose = 'download') => api.post(`/flyers/${id}/download`, { sizeSlug, purpose }, { responseType: 'blob' }),
  email: (id, sizeSlug) => api.post(`/flyers/${id}/email`, { sizeSlug }),
};

export const activity = {
  track: (data) => {
    if (readSupportSession() || isSupportTransitioning()) return Promise.resolve({ ok: true });
    const token = getActiveToken();
    return fetch(`${API_URL}/activity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify(data)
    });
  },
};

export const support = {
  start: (targetUserId, password, reason) => api.post('/support/sessions', { targetUserId, password, reason }),
  end: (id) => api.post(`/support/sessions/${id}/end`, {}, {
    supportTransitionAllowed: true,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  history: () => api.get('/support/history')
};

export const social = {
  getConnections: () => api.get('/social'),
  connect: (platform) => api.post(`/social/${platform}/connect`),
  disconnect: (platform) => api.delete(`/social/${platform}`),
  publish: (platform, data) => api.post(`/social/${platform}/publish`, data),
};

// Admin endpoints
export const admin = {
  getUsers: (params, config = {}) => api.get('/admin/users', { ...config, params }),
  getAdmins: () => api.get('/admin/admins'),
  getStaff: () => api.get('/admin/staff'),
  setStaffRole: (id, role) => api.put(`/admin/staff/${id}/role`, { role }),
  setStaffStatus: (id, isActive) => api.put(`/admin/staff/${id}/status`, { isActive }),
  sendStaffPasswordReset: (id) => api.post(`/admin/staff/${id}/reset-password`),
  getFlyerImports: () => api.get('/admin/flyer-imports'),
  createFlyerImport: (formData) => api.post('/admin/flyer-imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFlyerImport: (id) => api.get(`/admin/flyer-imports/${id}/source`, { responseType: 'blob' }),
  setFlyerImportStatus: (id, status) => api.put(`/admin/flyer-imports/${id}/status`, { status }),
  deleteFlyerImport: (id) => api.delete(`/admin/flyer-imports/${id}`),
  getUser: (id) => api.get(`/admin/users/${id}`),
  reassignUser: (id, adminId) => api.put(`/admin/users/${id}/assignment`, { adminId }),
  toggleUserStatus: (id, isActive) => api.put(`/admin/users/${id}/status`, { isActive }),
  sendPasswordReset: (id) => api.post(`/admin/users/${id}/reset-password`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getDashboard: (params = {}) => api.get('/admin/dashboard', { params }),
  getAnalytics: (params = {}, config = {}) => api.get('/admin/analytics', { ...config, params }),
  getAnalyticsDrilldown: (params = {}, config = {}) => api.get('/admin/analytics/drilldown', { ...config, params }),
  getNotifications: () => api.get('/admin/notifications'),
  markNotificationsRead: () => api.put('/admin/notifications/read-all'),
  getNotificationPreferences: () => api.get('/admin/notification-preferences'),
  updateNotificationPreferences: (preferences) => api.put('/admin/notification-preferences', preferences),
  exportActivity: (params) => api.get('/admin/export/activity', { params, responseType: 'blob' }),
  exportUsers: (params = {}) => api.get('/admin/export/users', { params, responseType: 'blob' }),
  createFlyer: (data) => api.post('/admin/flyers', data),
  updateFlyer: (id, data) => api.put(`/admin/flyers/${id}`, data),
  deleteFlyer: (id) => api.delete(`/admin/flyers/${id}`),
};

export default api;
